import { motion, useInView } from "framer-motion";
import { useRef } from "react";

const studies = [
  {
    tag: "Case Study · 01",
    title: "Activation Funnel Redesign",
    headline: "Activation rate improved from 42% to 61% in 12 weeks.",
    lead:
      "Led an onboarding redesign across web and mobile using weekly experiment cycles and end-to-end funnel instrumentation.",
    results: [
      "Time-to-first-value reduced by 37% (4.3 days → 2.7 days).",
      "Day-14 retention increased by 11 percentage points.",
      "Estimated +$420K annualized expansion revenue from faster activation.",
    ],
    tags: ["Onboarding", "Experimentation", "Growth"],
  },
  {
    tag: "Case Study · 02",
    title: "Support Triage Platform",
    headline: "Average resolution time dropped by 44% (19h → 10.6h).",
    lead:
      "Defined requirements with support, ops, and engineering, then shipped a routing and prioritization workflow in 8 weeks.",
    results: [
      "Escalation volume reduced by 29% after introducing queue ownership rules.",
      "First-response SLA compliance improved from 71% to 93%.",
      "Manual duplicate ticket handling reduced by 58%.",
    ],
    tags: ["Workflow", "Ops", "Prioritization"],
  },
  {
    tag: "Case Study · 03",
    title: "Product Operating Model for 4 Squads",
    headline: "Delivery predictability improved by 28% within 1 planning cycle.",
    lead:
      "Implemented quarterly planning, outcome-based roadmaps, and weekly KPI reviews across product, design, and engineering.",
    results: [
      "Roadmap spillover reduced from 34% to 18% quarter-over-quarter.",
      "Unplanned urgent work dropped by 22% due to clearer prioritization.",
      "Discovery allocation stabilized at 20% capacity each sprint.",
    ],
    tags: ["Operating Model", "Planning", "Leadership"],
  },
  {
    tag: "Case Study · 04",
    title: "Cross-Functional Metrics Dashboard",
    headline: "Adopted by 3 business functions in the first month.",
    lead:
      "Partnered with data engineering on trusted KPI definitions and built role-based views for product, growth, and leadership.",
    results: [
      "Weekly reporting prep time reduced from 6 hours to 90 minutes.",
      "Decision latency on growth experiments reduced by 35%.",
      "Metric discrepancy incidents dropped by 63% after KPI governance launch.",
    ],
    tags: ["Analytics", "KPI Governance", "Data"],
  },
];

const Card = ({ s, i }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-10%" });

  return (
    <motion.article
      ref={ref}
      initial={{ opacity: 0, y: 70 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 1.0, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
      className="relative rounded-2xl overflow-hidden border border-[var(--bone)]/10 bg-[var(--ink-2)]/60 backdrop-blur-sm p-6 sm:p-8 md:p-12"
      data-testid={`case-study-${i + 1}`}
    >
      <div className="flex items-center justify-between mb-8">
        <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-[var(--copper)]">
          {s.tag}
        </span>
        <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-[var(--bone-dim)]">
          {`0${i + 1} / 04`}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12">
        {/* Left — title + headline metric */}
        <div className="md:col-span-6">
          <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-[var(--bone-dim)] mb-4">
            {s.title}
          </div>
          <h3 className="font-serif text-2xl sm:text-3xl md:text-[3.4rem] leading-[1.05] md:leading-[1.02] text-[var(--bone)] break-words">
            {s.headline.split(" ").map((w, k) => {
              const isNum = /\d/.test(w);
              return (
                <span key={k} className={isNum ? "text-[var(--copper)] italic" : ""}>
                  {w}{" "}
                </span>
              );
            })}
          </h3>
        </div>

        {/* Right — lead + results */}
        <div className="md:col-span-6 md:pt-2">
          <p className="text-[var(--bone)]/75 text-base md:text-lg leading-relaxed font-light">
            {s.lead}
          </p>

          <ul className="mt-8 space-y-4 border-t border-[var(--bone)]/10 pt-8">
            {s.results.map((r, j) => (
              <li
                key={j}
                className="flex gap-4 text-[var(--bone)]/85 text-sm md:text-base leading-relaxed font-light"
              >
                <span className="font-mono text-[10px] text-[var(--copper)] mt-1.5 shrink-0">
                  {`0${j + 1}`}
                </span>
                <span>{r}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="mt-10 flex flex-wrap gap-2 pt-6 border-t border-[var(--bone)]/10">
        {s.tags.map((t) => (
          <span
            key={t}
            className="font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--bone-dim)]"
          >
            · {t}
          </span>
        ))}
      </div>
    </motion.article>
  );
};

const CaseStudies = () => {
  return (
    <section
      id="case-studies"
      className="relative py-28 md:py-40 bg-[var(--ink-2)]/30"
      data-testid="case-studies-section"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-20%" }}
          transition={{ duration: 0.9 }}
          className="mb-16 md:mb-24 max-w-3xl"
        >
          <div className="font-mono text-[11px] tracking-[0.28em] uppercase text-[var(--copper)] mb-4">
            [ Chapter IV — Case Studies ]
          </div>
          <h2 className="font-serif text-5xl md:text-7xl leading-[0.95] text-[var(--bone)]">
            The receipts,
            <br />
            <span className="italic text-[var(--bone-dim)]">itemised.</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 gap-6 md:gap-8">
          {studies.map((s, i) => (
            <Card s={s} i={i} key={s.tag} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default CaseStudies;
